import {
  CarrierVisitDirection,
  JourneyCategory,
  OrderResponseDto,
  OrderStatus,
  ReleaseResponseDto,
  WeightMeasurementUnit,
} from '@planning/app/api'
import { DashboardStatusField } from '@planning/components/visits-dashboard/VisitOrdersStatusRange'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import _ from 'lodash'

export const sortOrdersByContainerNumber = (orders: OrderResponseDto[]) => {
  return orders.sort((a, b) => (a.containerNumber ?? '').localeCompare(b.containerNumber ?? ''))
}

interface OrderProps {
  isOnTerminal?: boolean
  direction?: string
  hasBlockingHold?: boolean
  isReleaseExpired?: boolean
  allowGateInWhenUnitNotOnTerminal?: boolean
  skipReleaseOrder?: boolean
  noPlannedEquipment?: boolean
  isApproved?: boolean | null
  isFullfilled?: boolean
}
export const canOrderProceed = ({
  allowGateInWhenUnitNotOnTerminal,
  direction,
  hasBlockingHold,
  isOnTerminal,
  isReleaseExpired,
  skipReleaseOrder,
  noPlannedEquipment,
  isApproved,
  isFullfilled,
}: OrderProps): boolean => {
  const isValidOnTerminalState =
    direction === CarrierVisitDirection.Inbound
      ? !isOnTerminal
      : allowGateInWhenUnitNotOnTerminal || isFullfilled || isOnTerminal

  const isValidPlannedEquipmentState =
    !noPlannedEquipment || direction === CarrierVisitDirection.Outbound

  const isValidState =
    isValidOnTerminalState &&
    !hasBlockingHold &&
    (skipReleaseOrder || !isReleaseExpired) &&
    isValidPlannedEquipmentState &&
    (isApproved ?? true)

  return !!isValidState
}

export const canOrderGateIn = ({
  allowGateInWhenUnitNotOnTerminal,
  direction,
  hasBlockingHold,
  isOnTerminal,
  isReleaseExpired,
  skipReleaseOrder,
  noPlannedEquipment,
}: OrderProps): boolean => {
  const isValidOnTerminalState =
    direction === CarrierVisitDirection.Inbound
      ? !isOnTerminal
      : allowGateInWhenUnitNotOnTerminal || isOnTerminal

  const isValidPlannedEquipmentState =
    !noPlannedEquipment || direction === CarrierVisitDirection.Outbound

  const isValidState =
    isValidOnTerminalState &&
    !hasBlockingHold &&
    (skipReleaseOrder || !isReleaseExpired) &&
    isValidPlannedEquipmentState

  return !!isValidState
}

export interface VisitDashboardUnitTotalDefinition {
  teu?: number | null
  total?: number | null
  expected: DashboardStatusField
  onTerminal: DashboardStatusField
  offTerminal: DashboardStatusField
  full: number
  empty: number
}

export interface VisitDashboardGeneralCargoTotalDefinition {
  total?: number | null
  totalWeight?: number | null
  expected: DashboardStatusField
  onTerminal: DashboardStatusField
  offTerminal: DashboardStatusField
}

export const getVisitDashboardUnitInfoByOrders = (orders: IOrderItem[]) => {
  const totalDefinition = {
    total: 0,
    teu: 0,
    expected: {
      value: 0,
    },
    offTerminal: {
      value: 0,
    },
    onTerminal: {
      value: 0,
    },
    full: 0,
    empty: 0,
  }

  orders.forEach(order => {
    if (
      !(
        order.journeyCategory === JourneyCategory.Restow &&
        order.data.direction === CarrierVisitDirection.Outbound
      )
    ) {
      totalDefinition.total += 1
      totalDefinition.teu += order.data.containerLength && order.data.containerLength >= 30 ? 2 : 1

      if (order.data.isEmpty) totalDefinition.empty += 1
      else totalDefinition.full += 1

      if (order.containerJourney?.status === 'Departed') {
        totalDefinition.offTerminal.value += 1
      } else if (
        order.containerJourney?.status === 'OnTerminal' ||
        order.containerJourney?.status === 'OnYard'
      ) {
        totalDefinition.onTerminal.value += 1
      } else {
        totalDefinition.expected.value += 1
      }
    }
  })

  const archetypeGroups = getArchetypeGroups(orders, o => o.data.isoCodeArchetype)

  return {
    totalDefinition,
    archetypeGroups,
  }
}

export const getVisitDashboardGeneralCargoInfoByOrders = (orders: IOrderItem[]) => {
  const totalDefinition = {
    total: 0,
    totalWeight: 0,
    expected: {
      value: 0,
    },
    offTerminal: {
      value: 0,
    },
    onTerminal: {
      value: 0,
    },
  }

  orders.forEach(order => {
    if (
      !(
        order.journeyCategory === JourneyCategory.Restow &&
        order.data.direction === CarrierVisitDirection.Outbound
      )
    ) {
      const weightInKg = getWeightInKg(
        order.data.grossWeight,
        order.data.weightUnit || WeightMeasurementUnit.Kilogram,
      )

      totalDefinition.total += 1
      totalDefinition.totalWeight += weightInKg

      if (order.data.status === OrderStatus.Fulfilled) {
        if (order.data.direction === CarrierVisitDirection.Inbound) {
          totalDefinition.onTerminal.value += weightInKg
        } else {
          totalDefinition.offTerminal.value += weightInKg
        }
      } else {
        if (order.data.direction === CarrierVisitDirection.Inbound) {
          totalDefinition.expected.value += weightInKg
        } else {
          totalDefinition.onTerminal.value += weightInKg
        }
      }
    }
  })

  return {
    totalDefinition,
  }
}

export const getVisitOrderKPIStats = (orders: IOrderItem[]) => {
  const total = orders.length
  const completed = orders.filter(o => o.data.status === OrderStatus.Fulfilled).length
  const planned = total - completed

  return { total, completed, planned }
}

export const getVesselVisitKPIStats = (vesselVisitItem?: IVesselVisitItem) => {
  if (!vesselVisitItem) {
    return null
  }
  return {
    discharge: getVisitOrderKPIStats(vesselVisitItem.discharge.orders),
    load: getVisitOrderKPIStats(vesselVisitItem.load.orders),
    restow: getVisitOrderKPIStats(vesselVisitItem.restow.dischargeOrders),
  }
}
export interface IOrderArchetypeGroupModel {
  name: string | null
  total: number
}

type ArchetypeSelector<T> = (item: T) => string | null | undefined

const getArchetypeGroups = <T>(
  items: T[],
  selector: ArchetypeSelector<T>,
): IOrderArchetypeGroupModel[] => {
  if (items.length === 0) {
    return []
  }

  const grouped = _.groupBy(items, item => selector(item) ?? '__NULL__')

  return Object.entries(grouped).map(([key, value]) => ({
    name: key === '__NULL__' ? null : key,
    total: value.length,
  }))
}

export const formatOog = (
  order?: Pick<
    OrderResponseDto,
    | 'isOOG'
    | 'overHeight'
    | 'overWidthLeft'
    | 'overWidthRight'
    | 'overLengthFront'
    | 'overLengthRear'
  >,
): string | undefined => {
  if (!order?.isOOG) {
    return undefined
  }

  const { overHeight, overWidthLeft, overWidthRight, overLengthFront, overLengthRear } = order

  const height = overHeight ?? 0
  const widthLeft = overWidthLeft ?? 0
  const widthRight = overWidthRight ?? 0
  const lengthFront = overLengthFront ?? 0
  const lengthRear = overLengthRear ?? 0

  return `${height}x${widthLeft}x${widthRight}x${lengthFront}x${lengthRear} cm`
}

export const isOrderOnTerminal = (order: OrderResponseDto | ReleaseResponseDto) => {
  if (order && 'isOnTerminal' in order) {
    return !!order.isOnTerminal
  }

  return false
}

export const getWeightInKg = (
  grossWeight: number | null | undefined,
  weightUnit: WeightMeasurementUnit,
) => {
  if (!grossWeight) return 0

  return weightUnit === WeightMeasurementUnit.Kilogram ? grossWeight : grossWeight * 1000
}
